import classnames from "classnames";
import React from "react";
import * as _ from "lodash";
import { isBrowser, supportsObjectFit } from "../../utils/detection";

import "./LazyLoadImage.scss";

export interface ILazyLoadImageProps {
  className?: string;
  overlayStyle?: React.CSSProperties;
  src: IResponsiveImage;
  fallbackSrc?: string;
  alt: string;
  title?: string;
  children?: React.ReactNode;
}

/**
 * Our image pipeline outputs country and location images at these sizes
 */
const predefinedImageSizes = {
  _large: "1920w",
  _small: "512w",
};

/**
 * Generate a default src string - used in fallback logic in render method
 */
const getDefaultSrcString = (src: IResponsiveImage): string | undefined => {
  const locationOrContentImage = src as ILocationImage | IContentImage;
  const srcSet = src as ISrcSet;

  if (locationOrContentImage?.imageType != null) {
    return locationOrContentImage.largeUrl;
  }
  if (srcSet?.default != null && srcSet.srcs.length > 0) {
    return srcSet.default;
  }
  if (_.isString(src)) {
    return src as string;
  }
  return undefined;
};

/**
 * Takes an  ISrcSet | ILocationImage | IContentImage
 * Calls the relevant string generation functions - these both return srcset strings
 */
const getSrcSetString = (src: IResponsiveImage): string | undefined => {
  const image = src as ILocationImage | IContentImage;
  const srcSet = src as ISrcSet;

  // e.g. "example_small.png 512w, example_large.png 1920w
  if (image?.imageType != null) {
    const s = image.smallUrl ? `${encodeURI(image.smallUrl)} ${predefinedImageSizes._small}` : "/images/missing-image.jpg";
    const l = image.largeUrl ? `${encodeURI(image.largeUrl)} ${predefinedImageSizes._large}` : "/images/missing-image.jpg";
    return `${s}, ${l}`;
  }

  if (srcSet?.default != null && srcSet.srcs.length > 0) {
    return srcSet.srcs
      .reduce((a: string, c: ICustomSrcSet) => `${a} ${encodeURI(c.src.trim())} ${c.width}w,`, "")
      .replace(/,\s*$/, "");
  }

  return undefined;
}

/**
 * A lazyloading image component.
 *
 * N.B. Don't use CSS to target the contained <img> element! It will probably not work in IE11 - see #2791.
 * Only target .lazyload-image-container instead.
 *
 * @status stable
 */
export const LazyLoadImage = (props: ILazyLoadImageProps) => {
  const { alt, className, overlayStyle, title, src, fallbackSrc, children } = props;

  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const imageRef = React.useRef<HTMLImageElement | null>(null);

  // Re-trigger the fade effect when the src changes.
  React.useEffect(() => {
    if (imageRef.current != null) {
      imageRef.current.classList.remove("lazyload");
      imageRef.current.classList.add("lazyload");
    }
  }, [src]);

  const [aspectRatio, setAspectRatio] = React.useState<number | undefined>();
  const [fitToVertical, setFitToVertical] = React.useState<boolean | undefined>();
  const onImageLoad = React.useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    if (supportsObjectFit() || !e?.currentTarget || !containerRef?.current) {
      return;
    }

    // IE11 and some versions of Edge don't support object-fit
    // For these browsers, implement alternative layout
    // https://stackoverflow.com/a/47450998/268555
    // NB. this may break when window is resized
    // See also the --no-of class
    //
    // At this point, the image has been rendered onto the page so
    // we can grab its dimensions
    // NB. This assumption breaks if CSS targets <img> directly!
    const imageRatio = e.currentTarget?.clientHeight / e.currentTarget?.clientWidth;
    const containerRatio = containerRef?.current?.clientHeight / containerRef?.current?.clientWidth;

    setAspectRatio(imageRatio);
    setFitToVertical(containerRatio > imageRatio);
  }, []);

  const [imageLoadFailed, setImageLoadFailed] = React.useState(false);
  const onImageLoadFail = React.useCallback(() => setImageLoadFailed(true), [src]);

  const defaultSrc = getDefaultSrcString(src) || "/images/missing-image.jpg";
  const srcSetString = !isBrowser() ? getSrcSetString(src) : undefined; // Only use src set in browser

  const rootStyle: React.CSSProperties = {};
  const imgStyle: React.CSSProperties = {};
  if (isBrowser()) {
    if (imageLoadFailed) {
      rootStyle.backgroundImage = `url('${fallbackSrc ?? defaultSrc}')`
    }

    if (!supportsObjectFit() && fitToVertical != null) {
      if (fitToVertical) {
        imgStyle.width = "auto";
        imgStyle.height = "100%";
      }
      else {
        imgStyle.width = "100%";
        imgStyle.height = "auto";
      }
    }
  }

  return (
    <div className={classnames("lazyload-image-container", className)} ref={containerRef} style={rootStyle}>
      {(!supportsObjectFit() && aspectRatio != null) && (
        <div style={{
          // In a world without object fit, <img> is abs positioned
          // This is a way to insert an element of the same aspect ratio
          // so that layouts generally work in the same way.
          paddingTop: `${aspectRatio * 100}%`,
          pointerEvents: "none",
        }} />
      )}

      <img
        data-sizes="auto" // only useful for full width images
        data-src={encodeURI(defaultSrc)}
        data-srcset={srcSetString}
        className={classnames("lazyload", "lazyload-image-container__img")}
        style={imgStyle}
        ref={imageRef}
        alt={alt || ""}
        title={title}
        onLoad={onImageLoad}
        onError={onImageLoadFail}
      />

      <div className="lazyload-image-overlay" style={overlayStyle} />

      {children}
    </div>
  );
}
